/*
 * Description:
 * ---------------------
 * Firewall: whether this box is closed to everything but its own network,
 * the Weavian cloud and the management networks an operator lists
 * (cloud-anchor/pkg/firewall), and the device groups held to their places.
 * One header card carries the state, the only switch, the menu and Copy to
 * other sites; each section is its own component: Overview (the AI analyst,
 * then what the firewall does, drawn and listed), Rules (an operator's own
 * outbound and inbound, who always reaches the box and the management
 * networks, the known-bad lists, countries and flood limits), Intrusions
 * (bans of addresses that keep failing, FirewallIntrusions), Device groups,
 * Across sites and Activity (the record of blocked tries). The panel
 * makes the reads the sections share: the firewall, the device controls, what
 * watched groups reached, the analyst's findings, the devices on the LAN and
 * the jails; and shows a person whose own address is banned here how to get
 * back in (LetMeIn).
 *
 * Every change sends all the settings: the one being changed, and the rest as
 * the box last answered. The box keeps any a request leaves out, which is
 * what keeps an older console from clearing settings it does not know.
 *
 * Every change is in force at once and kept only once this console has read
 * the box back through the same path and confirmed it; a change that cut the
 * console off is never confirmed, and the box undoes it by itself. What the
 * screen shows is the appliance's own reading. All wording lives in
 * firewallWords.js.
 *
 * ! file components/FirewallPanel.js
 */
import React, { useEffect, useState } from "react";
import { FiActivity, FiAlertTriangle, FiClock, FiCopy, FiGlobe, FiGrid, FiLayers, FiList, FiRotateCcw, FiX } from "react-icons/fi";
import { TbShieldCheck, TbShieldLock, TbShieldOff, TbShieldX } from "react-icons/tb";
import useEdgeFeature, { FEATURE_ABSENT, FEATURE_PRESENT, FEATURE_UNREACHABLE } from "../hooks/useEdgeFeature";
import useEdgeWrite from "../hooks/useEdgeWrite";
import useVisibleInterval from "../hooks/useVisibleInterval";
import useDeviceControls from "../hooks/useDeviceControls";
import { IS_SINGLE_EDGE, nodeApiUrlFor } from "../serverApi";
import { confirmChange } from "../utils/confirmChange";
import FeatureUnavailableNotice from "./FeatureUnavailableNotice";
import FirewallOverview from "./FirewallOverview";
import FirewallGroups from "./FirewallGroups";
import FirewallAcross, { useFleetWatch } from "./FirewallAcross";
import FirewallActivity from "./FirewallActivity";
import FirewallCopy, { copyTargets } from "./FirewallCopy";
import { deviceRows, useLanDevices } from "./FirewallDevices";
import FirewallRules from "./FirewallRules";
import FirewallIntrusions, { LetMeIn, useIntrusions } from "./FirewallIntrusions";
import { useConfirm } from "./ConfirmModal";
import * as W from "./firewallWords";
import "./DeviceLimits.css";
import "./Firewall.css";

const SECTION_ICONS = { overview: FiGrid, rules: FiList, intrusions: TbShieldLock, groups: FiLayers, across: FiGlobe, activity: FiActivity };
const EMBLEMS = { on: TbShieldCheck, off: TbShieldOff, bad: TbShieldX };

/* The settings in force, whole, as a change sends them. */
export const settingsOf = (v) => ({
  on: !!v.on,
  management_networks: v.management_networks || [],
  blocklists: !!v.blocklists,
  block_countries: v.block_countries || [],
  rules: v.rules || [],
  log_server: v.log_server || "",
});

export default function FirewallPanel({ selectedNode, trafficClasses, nodes = [] }) {
  const { state, payload, refresh } = useEdgeFeature(selectedNode, "/firewall");
  const { busy, error, send } = useEdgeWrite(selectedNode, refresh);
  const confirm = useConfirm();
  const [lost, setLost] = useState("");
  const [section, setSection] = useState("overview");
  // The Rules tab's direction, here so another section can open it on one.
  const [rulesDir, setRulesDir] = useState("out");
  const go = (to, dir) => {
    if (dir) setRulesDir(dir);
    setSection(to);
  };
  const [dismissed, setDismissed] = useState(null);
  const [copying, setCopying] = useState(false);
  const view = state === FEATURE_PRESENT ? payload : null;
  // The sections' own reads, here so the menu and the overview can count them.
  const dc = useDeviceControls(selectedNode);
  const learn = useEdgeFeature(selectedNode, "/firewall/learn");
  const findings = useEdgeFeature(selectedNode, "/firewall/findings");
  const fleet = useFleetWatch(selectedNode);
  const lan = useLanDevices(selectedNode);
  const jails = useEdgeFeature(selectedNode, "/firewall/jails");
  const intrusions = useIntrusions(selectedNode);
  // Faster while a change waits, so the screen follows it to its end.
  useVisibleInterval(refresh, view?.change ? 3000 : 15000, !!view?.available);
  useVisibleInterval(learn.refresh, 30000, learn.state === FEATURE_PRESENT);
  useVisibleInterval(findings.refresh, 30000, findings.state === FEATURE_PRESENT);
  useVisibleInterval(jails.refresh, 15000, jails.state === FEATURE_PRESENT);
  // The switch counts down while a change waits for this console to confirm it.
  const [now, setNow] = useState(Date.now());
  const waitingUntil = view?.change?.until;
  useEffect(() => {
    if (!waitingUntil) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [waitingUntil]);

  if (!selectedNode) {
    return <div className="panel"><div className="panel-sub">Select a node to see its firewall.</div></div>;
  }
  if (state === FEATURE_ABSENT) {
    return (
      <FeatureUnavailableNotice nodeId={selectedNode} feature="The firewall"
        detail="This appliance is running a build from before the firewall shipped. Once it is
                updated, this screen fills in on its own. Until then the box filters nothing." />
    );
  }
  if (state === FEATURE_UNREACHABLE) {
    return (
      <div className="panel dl-warn">
        <div className="panel-title"><FiAlertTriangle size={17} /> Could not read the firewall from {selectedNode}</div>
        <div className="panel-sub">The request did not come back. The screen keeps trying and fills in on its own.</div>
      </div>
    );
  }
  if (!view) {
    return <div className="panel"><div className="panel-sub">Reading the firewall…</div></div>;
  }
  if (!view.available) {
    return (
      <div className="panel dl-warn">
        <div className="panel-title"><FiAlertTriangle size={17} /> The firewall cannot run on this appliance</div>
        <div className="panel-sub">{view.reason}</div>
      </div>
    );
  }

  /* Write, then confirm once the box answers a fresh read. */
  const apply = async (settings) => {
    setLost("");
    const out = await send("PUT", "/firewall", settings);
    if (!out) return false;
    try {
      await confirmChange(nodeApiUrlFor(selectedNode), out, "/firewall", "/firewall/confirm");
    } catch (e) {
      setLost(e.message);
    }
    refresh();
    return true;
  };
  const profiles = dc.view?.profiles || [];
  const rows = deviceRows(lan, dc.view?.devices || {});
  const settings = settingsOf(view);
  const turn = async (on) => {
    if (!(await confirm(on ? W.turnOnAsk(view) : W.TURN_OFF_ASK))) return;
    await apply({ ...settings, on });
  };
  /* The analyst's Block this place: a rule keeping every device from it,
     with the firewall turned on when it is off, since rules apply only then. */
  const blockPlace = async (f) => {
    const rule = W.blockRuleFor(f);
    if (!(await confirm(W.blockPlaceAsk(rule, view)))) return;
    await apply({ ...settings, on: true, rules: [...settings.rules, rule] });
  };

  const blocksSome = profiles.some((p) => Object.values(p.classes || {}).some((r) => r.block));
  const canCopy = !IS_SINGLE_EDGE && copyTargets(nodes, selectedNode).length > 0;
  const st = W.status(view, blocksSome);
  const Emblem = EMBLEMS[st.tone];
  // Across sites needs the portal's comparison; a console without it shows no such section.
  const sections = W.SECTIONS.filter((x) => !(x.portal && (IS_SINGLE_EDGE || fleet.available === false)));
  const shown = sections.some((x) => x.key === section) ? section : "overview";
  const tab = sections.find((x) => x.key === shown);
  const learned = learn.state === FEATURE_PRESENT && learn.payload?.available ? learn.payload : null;
  const jailed = jails.state === FEATURE_PRESENT && jails.payload?.available ? jails.payload : null;
  const badge = {
    rules: (view.rules || []).length || null,
    intrusions: (jailed?.bans || []).length || null,
    groups: profiles.length || null,
    across: fleet.odd.length || null,
  };
  const undone = view.undone && view.undone.id !== dismissed ? view.undone : null;
  const locked = busy || !!view.change;
  return (
    <div className="fw">
      <section className={`fw-top is-${st.tone}`}>
        <div className="fw-status">
          <span className="fw-emblem" aria-hidden="true"><Emblem size={34} strokeWidth={1.6} /></span>
          <div className="fw-status-text">
            <h2>{st.title}</h2>
            <p>{st.text}</p>
            <p className="fw-safety"><FiRotateCcw size={13} aria-hidden="true" /> {W.SAFETY}</p>
          </div>
          <button type="button" role="switch" aria-checked={!!view.on} aria-label="Firewall"
            className="fw-switch" disabled={locked} onClick={() => turn(!view.on)}>
            <span className="fw-switch-word">
              <small>{W.SWITCH_LABEL}</small>
              {view.change ? W.checkingWords(view, now) : view.on ? "On" : "Off"}
            </span>
            <span className="fw-track" aria-hidden="true"><span className="fw-knob" /></span>
          </button>
        </div>
        <div className="fw-bar">
          <nav className="fw-nav" role="tablist" aria-label="Firewall sections">
            {sections.map((x) => {
              const Icon = SECTION_ICONS[x.key];
              return (
                <button key={x.key} type="button" role="tab" aria-selected={x.key === shown} title={x.about}
                  className={x.key === shown ? "is-on" : undefined} onClick={() => setSection(x.key)}>
                  <Icon size={15} aria-hidden="true" />
                  {x.label}
                  {badge[x.key] != null && <em className={x.key === "across" ? "is-warn" : undefined}>{badge[x.key].toLocaleString()}</em>}
                </button>
              );
            })}
          </nav>
          {canCopy && (
            <button type="button" className="fw-btn is-quiet fw-copy" disabled={locked} onClick={() => setCopying(true)}>
              <FiCopy size={14} aria-hidden="true" /> {W.COPY.button}
            </button>
          )}
        </div>
        <p className="fw-tab-about"><strong>{tab.label}</strong> {tab.about}</p>
      </section>

      {!IS_SINGLE_EDGE && <LetMeIn selectedNode={selectedNode} intrusions={intrusions} />}
      {(error || lost) && <div className="fw-notice is-bad" role="alert"><FiAlertTriangle size={16} /> <span>{error || lost}</span></div>}
      {view.change && (
        <div className="fw-notice is-wait" role="status">
          <FiClock size={16} /> <span>{W.WAITING}</span>
        </div>
      )}
      {!view.change && undone && (
        <div className="fw-notice is-warn" role="note">
          <FiAlertTriangle size={16} /> <span>{W.undoneWords(view)}</span>
          <button type="button" className="fw-notice-x" aria-label="Dismiss" onClick={() => setDismissed(undone.id)}><FiX size={15} /></button>
        </div>
      )}
      {view.unreadable && <div className="fw-notice is-warn" role="note"><FiAlertTriangle size={16} /> <span>{W.UNREADABLE}</span></div>}

      {shown === "overview" && (
        <FirewallOverview view={view} jailed={jailed} controls={dc.view} learned={learned} fleet={fleet} findings={findings} rows={rows}
          selectedNode={selectedNode} go={go} locked={locked} onTurnOn={() => turn(true)} onBlockPlace={blockPlace} />
      )}
      {shown === "intrusions" && <FirewallIntrusions selectedNode={selectedNode} jails={jails} />}
      {shown === "rules" && (
        <FirewallRules view={view} settings={settings} groups={profiles} locked={locked} apply={apply} direction={rulesDir} onDirection={setRulesDir} />
      )}
      {shown === "groups" && (
        <FirewallGroups selectedNode={selectedNode} firewall={view} classList={trafficClasses || []} dc={dc} learn={learn} fleet={fleet} rows={rows} />
      )}
      {shown === "across" && <FirewallAcross fleet={fleet} here={selectedNode} go={go} />}
      {shown === "activity" && (
        <FirewallActivity selectedNode={selectedNode} view={view} jailed={jailed} settings={settings} rows={rows} groups={profiles} locked={locked} apply={apply} />
      )}
      {copying && (
        <FirewallCopy selectedNode={selectedNode} nodes={nodes} settings={settings} profiles={profiles} onClose={() => setCopying(false)} />
      )}
    </div>
  );
}
